import * as React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ListTodo } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface FeatureCardProps {
  id: string;
  name: string;
  description: string | null;
  taskCount: number;
  priority: "low" | "medium" | "high" | "urgent";
  onClick?: () => void;
}

const priorityConfig = {
  low: { label: "Low", className: "bg-muted text-muted-foreground" },
  medium: { label: "Medium", className: "bg-blue-500/10 text-blue-600" },
  high: { label: "High", className: "bg-amber-500/10 text-amber-600" },
  urgent: { label: "Urgent", className: "bg-red-500/10 text-red-600" },
};

export function FeatureCard({
  id,
  name,
  description,
  taskCount,
  priority,
  onClick,
}: FeatureCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const config = priorityConfig[priority];

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "cursor-grab transition-shadow hover:shadow-md active:cursor-grabbing",
        isDragging && "shadow-lg",
      )}
      onClick={onClick}
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-sm leading-tight font-medium text-foreground">{name}</h3>
          <span
            className={cn(
              "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
              config.className,
            )}
          >
            {config.label}
          </span>
        </div>
        {description && <p className="line-clamp-2 text-xs text-muted-foreground">{description}</p>}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ListTodo className="h-3.5 w-3.5" />
          <span>
            {taskCount} {taskCount === 1 ? "task" : "tasks"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Static version of the card for drag overlay
 */
export function FeatureCardOverlay({
  name,
  description,
  taskCount,
  priority,
}: Omit<FeatureCardProps, "id" | "onClick">) {
  const config = priorityConfig[priority];

  return (
    <Card className="w-[280px] rotate-2 shadow-lg">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-sm leading-tight font-medium text-foreground">{name}</h3>
          <span
            className={cn(
              "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
              config.className,
            )}
          >
            {config.label}
          </span>
        </div>
        {description && <p className="line-clamp-2 text-xs text-muted-foreground">{description}</p>}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ListTodo className="h-3.5 w-3.5" />
          <span>
            {taskCount} {taskCount === 1 ? "task" : "tasks"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
